import React, { useEffect } from "react";
import { useForm, Controller, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import TextEditorSection from "../../comman/TextEditor";
import {
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    ListItemText,
    FormHelperText,
    TextField,
} from "@mui/material";
import "react-datepicker/dist/react-datepicker.css";
import ReactDatePicker from "react-datepicker";
import GetImagesComponent from "./brandImages";

import './chatbot.css';

// Yup validation schema
const schema = yup.object().shape({
    preference: yup.string().required("Preference is required"),
    category_ids: yup.array().min(1, "Select at least one category"),
    country_ids: yup.array().min(1, "Select at least one country"),
    tier: yup.array().min(1, "Select at least one tier"),
    offer: yup
        .string()
        .max(72, "Offer cannot exceed 72 characters")
        .required("Offer is required"),
    brand: yup
        .string()
        .max(24, "Brand cannot exceed 24 characters")
        .required("Brand is required"),
    brand_id: yup
        .string()
        .max(6, "Brand ID cannot exceed 6 digits")
        .trim()
        .required("Brand ID is required"),
    creative_media: yup.string().url("Invalid URL").required("Creative Media URL is required"),
    detail_url: yup.string().url("Invalid URL").required("Detail URL is required"),
    backup_offer_detail: yup
        .string()
        .required("Backup Offer Detail is required"),
    terms_and_conditions: yup
        .string()
        .required("Terms and Conditions are required"),
    priority: yup.number().required("Priority is required").min(1).max(100 ,"Priority should be between 1 to 100"),
    validity: yup
        .string()
        .required("Validity date is required")
        .test("is-valid-date", "Invalid date", (value) => {
            return value && !isNaN(new Date(value).getTime());
        })
});

const defaultValue = {
    preference: "ONLINE",
    category_ids: [],
    country_ids: [],
    tier: [],
    offer: "",
    brand: "",
    brand_id: "",
    creative_media: "",
    detail_url: "",
    backup_offer_detail: "",
    terms_and_conditions: "",
    priority: 100,
    validity: "",
};

function OfferingForm({ onSubmit, updateData, resetForm }) {
    const { categories, countries, cards } = useSelector((state) => ({
        categories: [
            ...(state.categoryReducer?.categories?.data || []),
        ],
        countries: [
            ...(state.countryReducer?.countries?.data || []),
        ],
        cards: [
            ...(state.cardReducer?.cards?.data || []),
        ],
    }));



    const {
        control,
        handleSubmit,
        reset,
        setValue,
        watch,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(schema),
        defaultValues: defaultValue
    });


    //form reset after closing the popup
    useEffect(() => {
        if (resetForm) {
            reset(defaultValue);
        }
    }, [resetForm]);



    useEffect(() => {
        if (updateData) {
            reset({
                ...updateData,
                validity: updateData.validity
            });
        }
    }, [updateData, reset]);



    const selectedCategoryIds = useWatch({ control, name: "category_ids" });
    const selectedCategories = categories.filter((cat) =>
        selectedCategoryIds.includes(cat.id)
    );
    const suggestedCategoriesText = selectedCategories
        .map((cat) => cat.suggested_category)
        .join(", ");


    const handleSelectAll = (options, field, value) => {
        if (value.includes("all")) {
            if (field.value.length === options.length) {
                // If already all selected, clear all
                field.onChange([]);
            } else {
                // Select all
                field.onChange(options.map((o) => o.id));
            }
        } else {
            field.onChange(value);
        }
    };

    return (
        <div className="max-w-5xl mx-auto p-6">
            <form
                onSubmit={handleSubmit(onSubmit)}
                className="grid grid-cols-1 md:grid-cols-2 gap-6"
            >
                {/* -------- Left Section -------- */}
                <div className="space-y-4">
                    {/* Preference */}
                    <div>
                        <label className="block font-medium text-gray-700 mb-1">Preference</label>
                        <Controller
                            name="preference"
                            control={control}
                            render={({ field }) => (
                                <select {...field} className="w-full border rounded px-3 py-2">
                                    <option value="ONLINE">Online</option>
                                    <option value="OFFLINE">Offline</option>
                                </select>
                            )}
                        />
                        {errors.preference && (
                            <p className="text-red-500 text-sm">{errors.preference.message}</p>
                        )}
                    </div>

                    {/* Categories */}
                    <FormControl fullWidth error={!!errors.category_ids}>
                        <InputLabel id="category-label">Category</InputLabel>
                        <Controller
                            name="category_ids"
                            control={control}
                            render={({ field }) => (
                                <Select
                                    {...field}
                                    labelId="category-label"
                                    multiple
                                    renderValue={(selected) =>
                                        categories
                                            .filter((cat) => selected.includes(cat.id))
                                            .map((cat) => cat.category)
                                            .join(", ")
                                    }
                                    onChange={(e) =>
                                        handleSelectAll(categories, field, e.target.value)
                                    }
                                >
                                    <MenuItem value="all">
                                        <Checkbox checked={field.value?.length === categories.length} />
                                        <ListItemText primary="Select All" />
                                    </MenuItem>
                                    {categories.map((category) => (
                                        <MenuItem key={category.id} value={category.id}>
                                            <Checkbox
                                                checked={field.value?.includes(category.id) || false}
                                            />
                                            <ListItemText primary={category.category} />
                                        </MenuItem>
                                    ))}
                                </Select>
                            )}
                        />
                        {errors.category_ids && (
                            <FormHelperText>{errors.category_ids.message}</FormHelperText>
                        )}
                    </FormControl>

                    {/* Suggested Category */}
                    {selectedCategoryIds?.length > 1 && (
                        <div>
                            <label className="block font-medium text-gray-700 mb-1">
                                Suggested Category
                            </label>
                            <Controller
                                name="suggested_category"
                                control={control}
                                render={({ field }) => (
                                    <input
                                        {...field}
                                        type="text"
                                        value={suggestedCategoriesText}
                                        readOnly
                                        className="w-full border rounded px-3 py-2 bg-gray-100"
                                    />
                                )}
                            />
                        </div>
                    )}

                    {/* Countries */}
                    <FormControl fullWidth error={!!errors.country_ids}>
                        <InputLabel id="country-label">Country</InputLabel>
                        <Controller
                            name="country_ids"
                            control={control}
                            render={({ field }) => (
                                <Select
                                    {...field}
                                    labelId="country-label"
                                    multiple
                                    renderValue={(selected) =>
                                        countries
                                            .filter((c) => selected.includes(c.id))
                                            .map((c) => c.name)
                                            .join(", ")
                                    }
                                    onChange={(e) =>
                                        handleSelectAll(countries, field, e.target.value)
                                    }

                                >
                                    <MenuItem value="all">
                                        <Checkbox checked={field.value?.length === countries.length} />
                                        <ListItemText primary="Select All" />
                                    </MenuItem>
                                    {countries.map((c) => (
                                        <MenuItem key={c.id} value={c.id}>
                                            <Checkbox
                                                checked={field.value?.includes(c.id) || false}
                                            />
                                            <ListItemText primary={c.name} />
                                            {/* <Checkbox checked={field.value?.includes(c.id) || false} />
                                            <ListItemText primary={c.name} /> */}
                                        </MenuItem>
                                    ))}
                                </Select>
                            )}
                        />
                        {errors.country_ids && (
                            <FormHelperText>{errors.country_ids.message}</FormHelperText>
                        )}
                    </FormControl>

                    {/* Tier (multi-select) */}
                    <FormControl fullWidth error={!!errors.tier}>
                        <InputLabel id="tier-label">Tier</InputLabel>
                        <Controller
                            name="tier"
                            control={control}
                            render={({ field }) => (
                                <Select
                                    {...field}
                                    labelId="tier-label"
                                    multiple
                                    renderValue={(selected) =>
                                        cards
                                            .filter((c) => selected.includes(c.id))
                                            .map((c) => c.tier)
                                            .join(", ")
                                    }
                                    onChange={(e) =>
                                        handleSelectAll(cards, field, e.target.value)
                                    }
                                >
                                    <MenuItem value="all">
                                        <Checkbox checked={field.value?.length === cards.length} />
                                        <ListItemText primary="Select All" />
                                    </MenuItem>
                                    {cards.map((c) => (
                                        <MenuItem key={c.id} value={c.id}>
                                            <Checkbox checked={field.value?.includes(c.id) || false} />
                                            <ListItemText primary={c.tier} />
                                        </MenuItem>
                                    ))}
                                </Select>
                            )}
                        />
                        {errors.tier && (
                            <FormHelperText>{errors.tier.message}</FormHelperText>
                        )}
                    </FormControl>

                    {/* Offer */}
                    <div>
                        <label className="block font-medium text-gray-700 mb-1">Offer</label>
                        <Controller
                            name="offer"
                            control={control}
                            render={({ field }) => {
                                const length = field.value?.length || 0;
                                return (
                                    <>
                                        <input
                                            {...field}
                                            type="text"
                                            placeholder="Offer details"
                                            maxLength={74}
                                            className={`w-full border rounded px-3 py-2 ${length > 74 ? "border-red-500" : "border-gray-300"
                                                }`}
                                        />
                                        <p className={`mt-1 text-blue-500 text-sm font-medium mt-1 text-right ${length > 74 ? "text-red-500" : "text-gray-500"}`}>
                                            {length}/72
                                        </p>
                                    </>
                                );
                            }}
                        />
                        {errors.offer && (
                            <p className="text-red-500 text-sm">{errors.offer.message}</p>
                        )}
                    </div>

                    {/* Detail URL */}
                    <div>
                        <label className="block font-medium text-gray-700 mb-1">
                            Detail URL
                        </label>
                        <Controller
                            name="detail_url"
                            control={control}
                            render={({ field }) => (
                                <input
                                    {...field}
                                    type="url"
                                    placeholder="https://example.com/details"
                                    className="w-full border rounded px-3 py-2"
                                />
                            )}
                        />
                        {errors.detail_url && (
                            <p className="text-red-500 text-sm">
                                {errors.detail_url.message}
                            </p>
                        )}
                    </div>

                </div>

                {/* -------- Right Section -------- */}
                <div className="space-y-4">
                    {/* Brand */}
                    <div>
                        <label className="block font-medium text-gray-700 mb-1">Brand</label>
                        <Controller
                            name="brand"
                            control={control}
                            render={({ field }) => {
                                const length = field.value?.length || 0;
                                return (
                                    <>
                                        <input
                                            {...field}
                                            type="text"
                                            placeholder="Brand name"
                                            maxLength={24} // optional: you can remove if you want to allow typing more
                                            className={`w-full border rounded px-3 py-2 ${length > 24 ? "border-red-500" : "border-gray-300"
                                                }`}
                                        />
                                        <p className={`text-sm font-medium mt-1 text-blue-500 text-right ${length > 24 ? "text-red-500" : "text-gray-500"}`}>
                                            {length}/24
                                        </p>
                                    </>
                                );
                            }}
                        />
                        {errors.brand && (
                            <p className="text-red-500 text-sm">{errors.brand.message}</p>
                        )}
                    </div>


                    {/* Brand IDs (array input, comma-separated) */}
                    {/* <BrandIdsInput
                        name="brand_id"
                        control={control}
                        maxIds={6}
                        placeholder="Enter Brand IDs"
                    /> */}

                    {/* Brand IDs */}
                    
                    <GetImagesComponent
                        name="brand_id"
                        control={control}
                        errors={errors}
                        placeholder="Enter Brand ID..."
                        maxLength={6}
                        updateData={updateData ? true : false}
                        setValue={setValue}
                    />

                    {/* Creative Media */}
                    <div>
                        <label className="block font-medium text-gray-700 mb-1">
                            Creative Media URL
                        </label>
                        <Controller
                            name="creative_media"
                            control={control}
                            render={({ field }) => (
                                <input
                                    {...field}
                                    type="url"
                                    placeholder="https://example.com/banner.png"
                                    className="w-full border rounded px-3 py-2"
                                />
                            )}
                        />
                        {watch("creative_media") && (
                            <div className="flex justify-center items-center">
                                <img
                                    src={watch("creative_media")}
                                    alt="Creative Media"
                                    className="mt-2 w-48 h-32 object-cover rounded-lg shadow-md border transition-transform duration-300 hover:scale-105 hover:shadow-lg"
                                />
                            </div>
                        )}
                        {errors.creative_media && (
                            <p className="text-red-500 text-sm">
                                {errors.creative_media.message}
                            </p>
                        )}
                    </div>

                    {/* Validity Calendar */}
                    <div className="date-picker-parent">
                        <label className="block font-medium text-gray-700 mb-1 ">
                            Validity
                        </label>
                        <Controller
                            name="validity"
                            control={control}
                            render={({ field }) => {
                                const selectedDate = field.value ? new Date(field.value) : null;

                                return (
                                    <ReactDatePicker
                                        selected={selectedDate}                  // Date object for picker
                                        onChange={(date) => {
                                            if (date) {
                                                field.onChange(date.toISOString()); // store ISO string
                                            } else {
                                                field.onChange(null);
                                            }
                                        }}
                                        minDate={new Date()}                     // prevent past dates
                                        dateFormat="yyyy-MM-dd"
                                        className="w-full border rounded px-3 py-2"
                                        placeholderText="Select Validity Date"
                                        showMonthDropdown   // ✅ Month dropdown
                                        showYearDropdown    // ✅ Year dropdown
                                        dropdownMode="select" // ✅ Makes them real <select>
                                    />
                                );
                            }}
                        />
                        {errors.validity && (
                            <p className="text-red-500 text-sm">
                                {errors.validity.message}
                            </p>
                        )}
                    </div>


                    {/* Priority */}
                    <div>
                        <label className="block font-medium text-gray-700 mb-1">
                            Priority
                        </label>
                        <Controller
                            name="priority"
                            control={control}
                            render={({ field }) => (
                                <input
                                    {...field}
                                    type="number"
                                    min={1}
                                    placeholder="Priority"
                                    className="w-full border rounded px-3 py-2"
                                />
                            )}
                        />
                        {errors.priority && (
                            <p className="text-red-500 text-sm">{errors.priority.message}</p>
                        )}
                    </div>
                </div>

                {/* Backup Offer Detail & Terms */}
                <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <TextEditorSection
                            control={control}
                            name="backup_offer_detail"
                            label="Backup Offer Detail (Max 512 chars)"
                            maxLength={512}
                        />
                        {errors.backup_offer_detail && (
                            <p className="text-red-500 text-sm">
                                {errors.backup_offer_detail.message}
                            </p>
                        )}
                    </div>

                    <div>
                        <TextEditorSection
                            control={control}
                            name="terms_and_conditions"
                            label="Terms & Conditions (Max 512 chars)"
                            maxLength={512}
                        />
                        {errors.terms_and_conditions && (
                            <p className="text-red-500 text-sm">
                                {errors.terms_and_conditions.message}
                            </p>
                        )}
                    </div>
                </div>

                {/* Submit Button */}
                <div className="col-span-1 md:col-span-2 flex justify-center">
                    <button
                        type="submit"
                        className="w-50 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition"
                    >
                        {updateData ? "Update Offering" : "Add Offering"}
                    </button>
                </div>
            </form>
        </div>
    );
}

export default OfferingForm;
